import type {
  PermitListResponse,
  PermitResponse,
} from '@contracts/permitSearch/PermitListResponse';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { useClientLabel } from '@src/clients/hooks/useClientLabel';
import { Stack } from '@src/common/components/Stack';
import { Timestamp } from '@src/common/time/Timestamp';
import { PermitStatusBadge } from '@src/orders/components/PermitStatusBadge';
import { transformToPermitStatus } from '@src/orders/domain/PermitApplication';
import { PermitTypeFormatter } from '@src/permits/permitSearch/PermitTypeFormatter';
import { ViewablePermitTable } from '@src/permits/permitSearch/ViewablePermitTable';
import { useTranslation } from 'react-i18next';

interface Props {
  permits: PermitListResponse;
  isLoading: boolean;
  onSortModelChange: (sortModel: GridSortModel) => void;
  sortModel: GridSortModel;
}

export const SearchResult = ({
  permits,
  isLoading,
  onSortModelChange,
  sortModel,
}: Props) => {
  const { t } = useTranslation();
  const { clientLabel } = useClientLabel();

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: t('permitSearch.type'),
      flex: 2,
    },
    {
      field: 'permitName',
      headerName: t('permitSearch.name'),
      flex: 2,
    },
    {
      field: 'jurisdiction',
      headerName: t('permitSearch.jurisdiction'),
      flex: 1,
    },
    {
      field: 'stateId',
      headerName: t('permitSearch.stateId'),
      flex: 3,
    },
    {
      field: 'status',
      headerName: t('permitSearch.status'),
      flex: 1,
      renderCell: (cell) => getPermitStatusBadge(cell.value),
      minWidth: 150,
    },
    {
      field: 'agent',
      headerName: t('permitSearch.agent'),
      flex: 1,
    },
    {
      field: 'effectiveDate',
      headerName: t('permitSearch.effectiveDate'),
      flex: 1,
    },
    {
      field: 'submissionDate',
      headerName: t('permitSearch.submissionDate'),
      flex: 1,
    },
    {
      field: 'client',
      headerName: clientLabel,
      flex: 2,
      renderCell: (cell) => renderClientCell(cell.row),
    },
    {
      field: 'unitNumber',
      headerName: t('permitSearch.unitNumber'),
      flex: 1,
    },
    {
      field: 'plateNumber',
      headerName: t('permitSearch.plateNumber'),
      flex: 1,
    },
    {
      field: 'poNumber',
      headerName: t('permitSearch.PONumber'),
      flex: 1,
    },
  ];

  const getPermitStatusBadge = (stringStatus: string) => {
    const status = transformToPermitStatus(stringStatus);
    return <PermitStatusBadge status={status} />;
  };

  const formatPermitIds = (permit: PermitResponse) => {
    if (permit.statePermitNumber && permit.stateId) {
      return `${permit.stateId} / ${permit.statePermitNumber} `;
    }
    return permit.statePermitNumber ?? permit.stateId ?? '-';
  };

  const renderClientCell = (row: {
    client: string;
    clientDoingBusinessAs?: string;
  }) => {
    const hasDba = row.clientDoingBusinessAs;
    return (
      <Stack gap={0.5}>
        {hasDba ? (
          <>
            <Typography variant="body2">{row.clientDoingBusinessAs}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.client}
            </Typography>
          </>
        ) : (
          <Typography variant="body2">{row.client}</Typography>
        )}
      </Stack>
    );
  };

  const rows = permits.permits.map((permit) => ({
    id: permit.id,
    type: PermitTypeFormatter.format(permit.type.type),
    permitName: permit.type.name,
    jurisdiction: permit.type.jurisdiction,
    stateId: formatPermitIds(permit),
    status: permit.status,
    effectiveDate: Timestamp.from(permit.effectiveDate).formatUTCDate(),
    submissionDate: permit.submissionDate
      ? Timestamp.from(permit.submissionDate).formatUTCDate()
      : undefined,
    client: permit.client.name,
    clientDoingBusinessAs: permit.client.doingBusinessAs,
    unitNumber: permit.unitNumber,
    plateNumber: permit.plateNumber,
    orderId: permit.orderId,
    poNumber: permit.poNumber,
    agent: permit.claimedBy,
  }));

  return (
    <ViewablePermitTable
      onSortModelChange={onSortModelChange}
      rows={rows}
      columns={columns}
      sortModel={sortModel}
      isLoading={isLoading}
    />
  );
};
